import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import dynamic from "next/dynamic";
import classNames from "classnames";
import { uploadFile } from "@app/store/offersApi";
import { AppDispatch } from "@app/store/store";
import {
    useGetPublicProfileCompanyQuery,
    useUpdatePublicProfileCompanyMutation
} from "@entities/profile";
import { PublicCompanyProfile } from "@entities/profile/api/types";
import { GeoIcon } from "@shared/ui/Icon/ui/Profile/geo-icon";
import { VerifiedIcon } from "@shared/ui/Icon/ui/Profile/VerifiedIcon";
import { WaitingIcon } from "@shared/ui/Icon/ui/Profile/WaitingIcon";
import { TextInput } from "@shared/ui/inputs/text-input/text-input";
import { ERROR_DELETE_LOGO, ERROR_UPLOAD_LOGO } from "./constants";
import classes from "./company-info-form.module.scss";

const AvatarProfile = dynamic(
    () => import("./avatar-profile/avatar-profile").then((mod) => mod.AvatarProfile),
    { ssr: false }
);

export const CompanyInfoForm = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { setValue, watch, control, reset } = useForm({
        mode: "all"
    });

    const { data: publicCompanyData } = useGetPublicProfileCompanyQuery();
    const [updatePublicProfileCompany] = useUpdatePublicProfileCompanyMutation();

    const logoUrl = watch("logoUrl", "");

    const handleLogoChange = async (file: File | null) => {
        if (!file) return;

        setValue("logoUrl", URL.createObjectURL(file));

        try {
            const formData = new FormData();
            formData.append("file", file, "avatar.jpg");
            const { data } = await dispatch(uploadFile(formData)).unwrap();
            const uploadedUrl = data?.data?.big;

            if (!uploadedUrl) {
                toast.error(ERROR_UPLOAD_LOGO);
                return;
            }

            setValue("logoUrl", uploadedUrl);
            await updatePublicProfileCompany({ logoUrl: uploadedUrl });
        } catch {
            toast.error(ERROR_UPLOAD_LOGO);
        }
    };

    const handleLogoDelete = async () => {
        setValue("logoUrl", "");
        try {
            await updatePublicProfileCompany({ logoUrl: "" });
        } catch {
            toast.error(ERROR_DELETE_LOGO);
        }
    };

    const handleFieldBlur = (field: string, value: string | null) => {
        const current = publicCompanyData?.[field as keyof PublicCompanyProfile] ?? null;
        if (value !== current) {
            updatePublicProfileCompany({ [field]: value });
        }
    };

    useEffect(() => {
        if (publicCompanyData) {
            reset(publicCompanyData);
        }
    }, [publicCompanyData, reset]);

    return (
        <div className={classes["company-info"]}>
            <div className={classes["company-info__avatar"]}>
                <AvatarProfile
                    initialSrc={logoUrl}
                    onChange={handleLogoChange}
                    onDelete={handleLogoDelete}
                />
            </div>
            <Controller
                name="name"
                control={control}
                render={({ field }) => (
                    <TextInput
                        className={classes["company-info__name"]}
                        type="text"
                        placeholder="Введите название"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={() => handleFieldBlur("name", field.value)}
                    />
                )}
            />
            <Controller
                name="address"
                control={control}
                render={({ field }) => (
                    <div className={classes["company-info__location"]}>
                        <GeoIcon />
                        <TextInput
                            className={classes["company-info__location--input"]}
                            type="text"
                            placeholder="Введите адрес"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={() => handleFieldBlur("address", field.value)}
                        />
                    </div>
                )}
            />
            <div className={classes["company-info__status"]}>
                {publicCompanyData?.isVerified ? (
                    <>
                        <VerifiedIcon className={classes["company-card__status--icon"]} />
                        Проверенный аккаунт
                    </>
                ) : (
                    <>
                        <WaitingIcon className={classes["company-card__status--icon"]} />
                        Аккаунт на модерации
                    </>
                )}
            </div>
            <div className={classes["company-info__date"]}>
                <Controller
                    name="phone"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <PhoneInput
                            country={"ru"}
                            placeholder="+7 (999) 123-45-67"
                            value={value}
                            onChange={onChange}
                            onBlur={() => handleFieldBlur("phone", value)}
                            inputClass={classNames(classes["company-info__phone-input"], {
                                [classes["company-info__phone-input--fill"]]: Boolean(value)
                            })}
                            buttonClass={classNames(classes["company-info__phone-button"], {
                                [classes["company-info__phone-button--fill"]]: Boolean(value)
                            })}
                        />
                    )}
                />
                <Controller
                    name="url"
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            type="text"
                            placeholder="https://"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={() => handleFieldBlur("url", field.value)}
                            className={classNames(classes["company-info__input"], {
                                [classes["company-info__input--fill"]]: Boolean(field.value)
                            })}
                        />
                    )}
                />
            </div>
        </div>
    );
};